import { HttpClient, HttpContext } from '@angular/common/http';
import { EMPTY, expand, map, type Observable, toArray } from 'rxjs';
import type { ListResponse } from './resource.types';

const DEFAULT_PAGE_SIZE = 200;

function toStringArray(value: string | string[] | undefined): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

/**
 * Configuration for paginated fetching.
 */
export interface FetchAllPagesConfig {
  /** Full API path (e.g. '/api/v1/core/skills') */
  basePath: string;
  /** Extra query parameters forwarded to every request */
  params?: Record<string, string>;
  /** Sort expression(s) forwarded as `sort` query param (e.g. 'name:asc') */
  sort?: string | string[];
  /** Search expression(s) forwarded as `search` query param (e.g. 'name:term') */
  search?: string | string[];
  /** Filter expression(s) forwarded as `filter` query param */
  filter?: string | string[];
  /** Items per page (default 200 — the backend max) */
  pageSize?: number;
  /** HttpContext forwarded to every page request (e.g. SKIP_FORBIDDEN_TOAST) */
  context?: HttpContext;
}

/**
 * Builds a URL for a single page request.
 */
function buildPageUrl(config: FetchAllPagesConfig, skip: number, take: number): string {
  const params = new URLSearchParams({ skip: String(skip), take: String(take) });

  for (const [key, value] of Object.entries(config.params ?? {})) {
    params.set(key, value);
  }

  const sortValues = toStringArray(config.sort);
  for (const sort of sortValues) {
    params.append('sort', sort);
  }

  const searchValues = toStringArray(config.search);
  for (const search of searchValues) {
    params.append('search', search);
  }

  const filterArray = toStringArray(config.filter);
  for (const f of filterArray) {
    params.append('filter', f);
  }

  return `${config.basePath}?${params.toString()}`;
}

/**
 * Fetches all pages of a paginated list endpoint by following `meta.hasMore`.
 *
 * Uses `expand()` to issue sequential page requests until the server reports
 * no more data. The result is a single flat array containing every item.
 *
 * @example
 * ```typescript
 * fetchAllPages<Skill>(http, {
 *   basePath: '/api/v1/core/skills',
 *   sort: 'name:asc',
 * }).subscribe(skills => console.log(skills.length));
 * ```
 */
export function fetchAllPages<T>(http: HttpClient, config: FetchAllPagesConfig): Observable<T[]> {
  const pageSize = config.pageSize ?? DEFAULT_PAGE_SIZE;

  const loadPage = (skip: number): Observable<ListResponse<T>> =>
    http.get<ListResponse<T>>(buildPageUrl(config, skip, pageSize), config.context ? { context: config.context } : {});

  return loadPage(0).pipe(
    expand(response => {
      if (!response.meta.hasMore) {
        return EMPTY;
      }

      const nextSkip = response.meta.skip + response.meta.count;
      if (nextSkip <= response.meta.skip) {
        throw new Error(`Pagination did not advance: next skip ${nextSkip} is not greater than current skip ${response.meta.skip}`);
      }

      return loadPage(nextSkip);
    }),
    toArray(),
    map(responses => responses.flatMap(response => response.data))
  );
}

/**
 * Fetches a single page of a paginated list endpoint.
 *
 * Useful for cases where only one page is needed (e.g. dropdown lazy-load
 * triggered by scroll).
 */
export function fetchPage<T>(http: HttpClient, config: FetchAllPagesConfig, skip: number, take?: number): Observable<ListResponse<T>> {
  return http.get<ListResponse<T>>(
    buildPageUrl(config, skip, take ?? config.pageSize ?? DEFAULT_PAGE_SIZE),
    config.context ? { context: config.context } : {}
  );
}
